import type { MakeErrorMessageFn, ResponseInterceptorConfig } from './types'
import { isFunction } from '@vue/shared'
import axios from 'axios'
import { startLoading, endLoading } from './modules/loading'
import { getToken } from '@el-plus/utils/cookie'
import { useLocale } from '@el-plus/hooks/use-locale'
const { t } = useLocale()

export const defaultRequestInterceptor = () => {
  return {
    fulfilled: async (config) => {
      if (config.loading) {
        startLoading()
      }
      const token = getToken()
      config.headers.Authorization = token ? `Bearer ${token}` : null
      config.headers = {
        ...config.headers,
        ...window.hx.getSignHeaders({
          sign: window.$EP_SIGN || {},
          config,
        }),
      }
      return config
    },
    rejected: (error) => {
      return Promise.reject(error)
    },
  }
}
export const defaultResponseInterceptor = (options?: {
  /** 响应数据中代表访问结果的字段名 */
  codeField?: string
  /** 响应数据中装载实际数据的字段名，或者提供一个函数从响应数据中解析需要返回的数据 */
  dataField?: ((response: any) => any) | string
  /** 当codeField所指定的字段值与successCode相同时，代表接口访问成功。如果提供一个函数，则返回true代表接口访问成功 */
  successCode?: ((code: any) => boolean) | number | string
  makeInfoMessage?: MakeErrorMessageFn
}): ResponseInterceptorConfig => {
  const {
    codeField = 'code',
    dataField = 'data',
    successCode = 200,
    makeInfoMessage,
  } = options || {}
  return {
    fulfilled: (response) => {
      const { config, data: responseData, status } = response
      if (config.loading) {
        endLoading()
      }
      if (config.responseReturn === 'raw') {
        return response
      }
      if (!responseData.success && !config.noErrorMsg) {
        const msg = responseData.msg || responseData.message || 'Error'
        const error = new Error(msg) as Error & { type: string }
        error.type = 'service-error'
        makeInfoMessage?.(msg, error)
        return Promise.reject(error)
      }

      if (status >= 200 && status < 400) {
        if (config.responseReturn === 'body') {
          return responseData
        } else if (
          isFunction(successCode)
            ? successCode(responseData[codeField])
            : responseData[codeField] === successCode
        ) {
          return isFunction(dataField)
            ? dataField(responseData)
            : responseData[dataField]
        }
      }
      throw Object.assign({}, response, { response })
    },
  }
}

// 错误信息拦截器
export const errorMessageResponseInterceptor = (
  makeErrorMessage?: MakeErrorMessageFn,
): ResponseInterceptorConfig => {
  return {
    rejected: (error: any) => {
      endLoading()
      if (axios.isCancel(error) || error.type === 'service-error') {
        return Promise.reject(error)
      }

      const err: string = error?.toString?.() ?? ''
      let errMsg = ''
      if (err?.includes('Network Error')) {
        errMsg = t('ep.http.networkError')
      } else if (error?.message?.includes?.('timeout')) {
        errMsg = t('ep.http.timeout')
      }
      if (errMsg) {
        makeErrorMessage?.(errMsg, error)
        return Promise.reject(error)
      }

      let errorMessage = ''
      const status = error?.response?.status
      switch (status) {
        case 400: {
          errorMessage = t('ep.http.requestError')
          break
        }
        case 401: {
          errorMessage = t('ep.http.authExpired')
          break
        }
        case 403: {
          errorMessage = t('ep.http.forbidden')
          break
        }
        case 404: {
          errorMessage = t('ep.http.notFound')
          break
        }
        default: {
          errorMessage = t('ep.http.serverError')
        }
      }
      makeErrorMessage?.(errorMessage, error)
      return Promise.reject(error)
    },
  }
}
